import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);

/** Shared fullscreen zoom for product media and catalog pages. */
(() => {
  if (window.__ansterImageZoomReady) return;
  window.__ansterImageZoomReady = true;

  const style = document.createElement('style');
  style.textContent = '.anster-zoom-toolbar{position:fixed;left:50%;bottom:max(20px,env(safe-area-inset-bottom));transform:translateX(-50%);display:flex;align-items:center;gap:4px;padding:6px;border-radius:999px;background:rgba(15,23,42,.88);box-shadow:0 12px 35px rgba(0,0,0,.28);z-index:2147483646}.anster-zoom-toolbar button{width:42px;height:42px;border:0;border-radius:999px;background:#fff;color:#111827;font:700 18px/1 Arial;cursor:pointer}.anster-zoom-toolbar button[data-anster-zoom-reset]{width:66px;font-size:12px}.anster-zoomable-image{touch-action:none!important;user-select:none;transform-origin:center center;will-change:transform;cursor:zoom-in}.anster-zoomable-image.is-zoomed{cursor:grab}.anster-zoomable-image.is-dragging{cursor:grabbing}.anster-catalog-zoom-dialog{width:100vw;height:100dvh;max-width:none;max-height:none;margin:0;padding:0;border:0;background:rgba(4,10,24,.96);overflow:hidden}.anster-catalog-zoom-dialog::backdrop{background:rgba(4,10,24,.96)}.anster-catalog-zoom-dialog__image{display:block;width:auto;height:auto;max-width:92vw;max-height:88dvh;position:absolute;inset:50% auto auto 50%}.anster-catalog-zoom-dialog__close,.anster-catalog-zoom-dialog__nav{position:fixed;z-index:2147483646;border:0;border-radius:999px;background:rgba(255,255,255,.94);color:#111827;cursor:pointer}.anster-catalog-zoom-dialog__close{top:max(16px,env(safe-area-inset-top));right:16px;width:46px;height:46px;font-size:28px}.anster-catalog-zoom-dialog__nav{top:50%;width:48px;height:48px;font-size:32px;transform:translateY(-50%)}.anster-catalog-zoom-dialog__nav--prev{left:14px}.anster-catalog-zoom-dialog__nav--next{right:14px}.anster-catalogs__page{cursor:zoom-in}.anster-catalogs__page:focus-visible{outline:3px solid #f97316;outline-offset:4px}@media(max-width:749px){.anster-zoom-toolbar{bottom:max(12px,env(safe-area-inset-bottom))}.anster-zoom-toolbar button{width:40px;height:40px}.anster-catalog-zoom-dialog__nav{top:auto;bottom:76px}.anster-catalog-zoom-dialog__image{max-width:96vw;max-height:82dvh}}';
  style.textContent += '.anster-product-gallery-dialog{position:relative!important;overflow:hidden!important}.anster-product-gallery-dialog__close{position:absolute!important;top:16px!important;right:16px!important;z-index:2147483647!important;display:grid!important;place-items:center;opacity:1!important;visibility:visible!important;pointer-events:auto!important}.anster-catalog-zoom-dialog__close{position:fixed!important;top:max(14px,env(safe-area-inset-top))!important;right:14px!important;z-index:2147483647!important;display:grid!important;place-items:center;opacity:1!important;visibility:visible!important;pointer-events:auto!important}.anster-product-gallery-dialog__image,.anster-catalog-zoom-dialog__image{z-index:1!important}.anster-product-gallery-dialog__nav,.anster-catalog-zoom-dialog__nav,.anster-zoom-toolbar{z-index:2147483646!important}.anster-zoomable-image{transition:transform .14s ease-out}.anster-zoomable-image.is-dragging{transition:none!important}';
  style.textContent += '@media(max-width:749px){.anster-catalogs__item[data-slug=\"catalog-anster-tooling\"] b{font-size:8.7px!important;line-height:1.08!important;letter-spacing:-.015em}}';
  style.textContent += '@media(max-width:749px){.anster-product-rail-card{flex:0 0 177px!important;width:177px!important;min-width:177px!important;height:252px!important}.anster-product-rail-card__media{height:126px!important;padding:6px!important}.anster-product-rail-card__media img{width:100%!important;height:100%!important;object-fit:contain!important}.anster-product-rail-card__body{height:124px!important;min-height:124px!important;padding:9px 10px 11px!important}.anster-product-rail-card__body strong{font-size:14px!important;line-height:1.15!important;display:-webkit-box!important;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}.anster-product-rail-card__meta{gap:5px!important}.anster-product-rail-card__series,.anster-product-rail-card__article{font-size:10px!important;padding:4px 6px!important}.anster-product-rail-card__price{font-size:11px!important}.anster-product-rail__track{grid-auto-columns:177px!important;gap:10px!important;scroll-padding-inline:0!important}}.anster-product-rail:not(:has(.anster-product-rail-card)){display:none!important}';
  document.head.appendChild(style);

  const fullImageUrl = (source) => {
    try {
      const url = new URL(source, window.location.href);
      url.searchParams.delete('width');
      return url.href;
    } catch (_) {
      return source;
    }
  };

  const enhanceZoom = (dialog, image) => {
    if (!dialog || !image || image.dataset.ansterZoomReady) return;
    image.dataset.ansterZoomReady = 'true';
    image.classList.add('anster-zoomable-image');
    image.draggable = false;
    let scale = 1;
    let x = 0;
    let y = 0;
    let targetX = 0;
    let targetY = 0;
    let frame = 0;
    let startScale = 1;
    let startDistance = 0;
    let moved = false;
    const pointers = new Map();

    const toolbar = document.createElement('div');
    toolbar.className = 'anster-zoom-toolbar';
    toolbar.setAttribute('aria-label', 'Масштаб фото');
    toolbar.innerHTML = '<button type="button" data-anster-zoom-out aria-label="Зменшити масштаб">−</button><button type="button" data-anster-zoom-reset aria-label="Скинути масштаб">100%</button><button type="button" data-anster-zoom-in aria-label="Збільшити масштаб">+</button>';
    dialog.appendChild(toolbar);
    ['click', 'pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach((type) => toolbar.addEventListener(type, (event) => event.stopPropagation()));
    const percentage = toolbar.querySelector('[data-anster-zoom-reset]');

    const applyTransform = () => {
      frame = 0;
      x = targetX;
      y = targetY;
      image.style.transform = image.classList.contains('anster-catalog-zoom-dialog__image')
        ? 'translate(calc(-50% + ' + x + 'px), calc(-50% + ' + y + 'px)) scale(' + scale + ')'
        : 'translate(' + x + 'px,' + y + 'px) scale(' + scale + ')';
      image.classList.toggle('is-zoomed', scale > 1.01);
      percentage.textContent = Math.round(scale * 100) + '%';
    };
    const scheduleTransform = () => {
      if (!frame) frame = requestAnimationFrame(applyTransform);
    };
    const reset = () => {
      scale = 1;
      targetX = 0;
      targetY = 0;
      pointers.clear();
      scheduleTransform();
    };
    const setScale = (value) => {
      scale = Math.max(1, Math.min(5, value));
      if (scale === 1) { targetX = 0; targetY = 0; }
      scheduleTransform();
    };
    const toggleZoom = () => setScale(scale > 1.01 ? 1 : 2);

    toolbar.querySelector('[data-anster-zoom-in]').addEventListener('click', () => setScale(scale + .5));
    toolbar.querySelector('[data-anster-zoom-out]').addEventListener('click', () => setScale(scale - .5));
    percentage.addEventListener('click', reset);
    image.addEventListener('dragstart', (event) => event.preventDefault());
    image.addEventListener('click', () => {
      if (moved) { moved = false; return; }
      toggleZoom();
    });
    image.addEventListener('wheel', (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? .25 : -.25));
    }, { passive: false });
    image.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      image.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      moved = false;
      image.classList.add('is-dragging');
      if (pointers.size === 2) {
        const values = Array.from(pointers.values());
        startDistance = Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
        startScale = scale;
      }
    });
    image.addEventListener('pointermove', (event) => {
      const previous = pointers.get(event.pointerId);
      if (!previous) return;
      event.preventDefault();
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2) {
        const values = Array.from(pointers.values());
        const distance = Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
        if (startDistance) setScale(startScale * distance / startDistance);
      } else if (scale > 1) {
        const dx = event.clientX - previous.x;
        const dy = event.clientY - previous.y;
        if (Math.abs(dx) + Math.abs(dy) > 1) moved = true;
        targetX += dx;
        targetY += dy;
        scheduleTransform();
      }
    });
    const release = (event) => {
      pointers.delete(event.pointerId);
      if (!pointers.size) image.classList.remove('is-dragging');
      if (pointers.size < 2) startDistance = 0;
    };
    image.addEventListener('pointerup', release);
    image.addEventListener('pointercancel', release);
    if (image.classList.contains('anster-catalog-gallery-dialog__image')) {
      dialog.querySelectorAll('.anster-catalog-gallery-dialog__arrow').forEach((button) => button.addEventListener('click', reset));
    } else {
      let lastImageSource = image.currentSrc || image.src;
      new MutationObserver(() => {
        const nextImageSource = image.currentSrc || image.src;
        if (nextImageSource !== lastImageSource) { lastImageSource = nextImageSource; reset(); }
      }).observe(image, { attributes: true, attributeFilter: ['src'] });
    }
    dialog.querySelectorAll('[data-anster-gallery-prev],[data-anster-gallery-next]').forEach((button) => button.addEventListener('click', reset));
    dialog.addEventListener('close', reset);
    applyTransform();
  };

  const init = () => {
    const productDialog = document.querySelector('[data-anster-product-gallery-dialog]');
    const productImage = productDialog?.querySelector('[data-anster-gallery-image]');
    if (productDialog && productImage) enhanceZoom(productDialog, productImage);

    let catalogImages = [];
    let catalogIndex = 0;
    const catalogDialog = document.createElement('dialog');
    catalogDialog.className = 'anster-catalog-zoom-dialog';
    catalogDialog.innerHTML = '<button class="anster-catalog-zoom-dialog__close" type="button" aria-label="Закрити">×</button><button class="anster-catalog-zoom-dialog__nav anster-catalog-zoom-dialog__nav--prev" type="button" aria-label="Попередня сторінка">‹</button><img class="anster-catalog-zoom-dialog__image" alt=""><button class="anster-catalog-zoom-dialog__nav anster-catalog-zoom-dialog__nav--next" type="button" aria-label="Наступна сторінка">›</button>';
    document.body.appendChild(catalogDialog);
    const catalogImage = catalogDialog.querySelector('img');
    const showCatalogImage = (index) => {
      if (!catalogImages.length) return;
      catalogIndex = (index + catalogImages.length) % catalogImages.length;
      const source = catalogImages[catalogIndex];
      catalogImage.src = fullImageUrl(source.currentSrc || source.src);
      catalogImage.alt = source.alt || 'Сторінка каталогу';
    };
    const openCatalogImage = (figure) => {
      catalogImages = Array.from(document.querySelectorAll('.anster-catalogs__page img'));
      const selected = figure.querySelector('img');
      showCatalogImage(Math.max(0, catalogImages.indexOf(selected)));
      if (!catalogDialog.open) catalogDialog.showModal();
    };
    document.addEventListener('click', (event) => {
      const figure = event.target.closest?.('.anster-catalogs__page');
      if (!figure) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openCatalogImage(figure);
    }, true);
    document.addEventListener('keydown', (event) => {
      const figure = event.target.closest?.('.anster-catalogs__page');
      if (!figure || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openCatalogImage(figure);
    }, true);
    catalogDialog.querySelector('.anster-catalog-zoom-dialog__close').addEventListener('click', () => catalogDialog.close());
    catalogDialog.querySelector('.anster-catalog-zoom-dialog__nav--prev').addEventListener('click', () => showCatalogImage(catalogIndex - 1));
    catalogDialog.querySelector('.anster-catalog-zoom-dialog__nav--next').addEventListener('click', () => showCatalogImage(catalogIndex + 1));
    catalogDialog.addEventListener('click', (event) => { if (event.target === catalogDialog) catalogDialog.close(); });
    enhanceZoom(catalogDialog, catalogImage);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

/** Let desktop category labels wrap without changing the product grid. */
(() => {
  const style = document.createElement('style');
  style.textContent = '@media (min-width: 750px) { .anster-catalog-layout--assortment .anster-category-sidebar__link { box-sizing: border-box; min-width: 0; width: 100%; white-space: normal; overflow-wrap: break-word; } }';
  document.head.appendChild(style);
})();

/** Use a dedicated full-width assortment banner on phones only. */
(() => {
  const initMobileAssortmentBanner = () => {
    if (window.location.pathname !== '/collections/all' || !window.matchMedia('(max-width: 749px)').matches) return;
    const hero = document.querySelector('.anster-collection-hero--banner');
    const image = hero?.querySelector('img');
    if (!hero || !image) return;
    image.src = 'https://anster.com.ua/cdn/shop/files/anster-assortment-mobile-banner-v2.png';
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    hero.style.width = '100%';
    hero.style.height = 'clamp(150px, 42vw, 190px)';
    hero.style.overflow = 'hidden';
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.objectFit = 'cover';
    image.style.objectPosition = 'center center';
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initMobileAssortmentBanner, { once: true });
  else initMobileAssortmentBanner();
})();

/** Prevent the unsorted assortment grid from flashing before its saved order is applied. */
if (window.location.pathname === '/collections/all') {
  const style = document.createElement('style');
  style.textContent = 'html:not(.anster-assortment-ready) .anster-catalog-layout--assortment .product-grid { visibility: hidden; }';
  document.head.appendChild(style);
  const reveal = () => document.documentElement.classList.add('anster-assortment-ready');
  const afterLayout = () => requestAnimationFrame(() => requestAnimationFrame(reveal));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', afterLayout, { once: true });
  else afterLayout();
  setTimeout(reveal, 1500);
}
/** Add a subtle desktop-only gap below the assortment banner. */
(() => {
  const style = document.createElement('style');
  style.textContent = '@media (min-width: 750px) { .anster-collection-hero--banner ~ .anster-catalog-layout { margin-top: 8px !important; } }';
  document.head.appendChild(style);
})();

/** Add a touch more orange space to series labels on mobile cards. */
(() => {
  const style = document.createElement('style');
  style.textContent = '@media (max-width: 749px) { .anster-card-meta-row { flex-wrap: nowrap !important; } .anster-card-meta-row .anster-series-badge { flex: 0 0 auto !important; width: max-content !important; max-width: 100% !important; overflow: visible !important; padding-right: 10px !important; } }';
  document.head.appendChild(style);
})();

/** Normalize Cyrillic lookalikes in article searches. *//** Add a touch more orange space to series labels on mobile cards. */
(() => {
  const style = document.createElement('style');
  style.textContent = '@media (max-width: 749px) { .anster-card-meta-row { flex-wrap: nowrap !important; } .anster-card-meta-row .anster-series-badge { flex: 0 0 auto !important; width: max-content !important; max-width: 100% !important; overflow: visible !important; padding-right: 10px !important; } }';
  document.head.appendChild(style);
})();

/** Normalize Cyrillic lookalikes in article searches. */
const ansterNormalizeArticleSearch=(v)=>{if(!/\d/.test(v)||!/[А-Яа-яІіЇїЄєҐґ]/.test(v))return v;const m={а:'a',в:'b',с:'c',е:'e',н:'h',і:'i',к:'k',м:'m',о:'o',р:'p',т:'t',х:'x',у:'y'};return v.replace(/[авсенікмортху]/gi,l=>{const n=m[l.toLocaleLowerCase('uk')];return l===l.toUpperCase()?(n?.toUpperCase()??l):(n??l)})};
document.addEventListener('input',e=>{const i=e.target;if(!(i instanceof HTMLInputElement)||i.type!=='search')return;const n=ansterNormalizeArticleSearch(i.value);if(n!==i.value){const p=i.selectionStart;i.value=n;if(p!==null)i.setSelectionRange(p,p)}},true);
}

